import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { UserService, User } from '../../services/user';
import { createUserIdToNameMap, resolveTeamName } from '../../utils/userNameResolver';

interface TeamWorkloadDistributionChartProps {
  data: Array<{
    team_name: string | number;
    workshop_count: number;
    percentage: number;
    workload_score: number;
    active_mentors: number;
  }>;
  loading?: boolean;
}

const TeamWorkloadDistributionChart: React.FC<TeamWorkloadDistributionChartProps> = ({ data, loading = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await UserService.getAllDirectoryUsers();
        setUsers(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Create a map of user ID to user name
  const userIdToNameMap = useMemo(() => createUserIdToNameMap(users), [users]);

  // Resolve team names (convert IDs to names)
  const resolvedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      team_name: resolveTeamName(item.team_name, userIdToNameMap)
    }));
  }, [data, userIdToNameMap]);
  if (loading || usersLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Workload Distribution</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Workload Distribution</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No workload distribution data available
        </div>
      </div>
    );
  }

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getWorkloadLevel = (score: number) => {
    if (score >= 80) return { level: 'High', color: '#ef4444' };
    if (score >= 60) return { level: 'Medium', color: '#f59e0b' };
    if (score >= 40) return { level: 'Normal', color: '#3b82f6' };
    return { level: 'Low', color: '#10b981' };
  };

  const totalWorkshops = resolvedData.reduce((sum, item) => sum + item.workshop_count, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Workload Distribution</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={resolvedData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ team_name, percentage }: any) => `${team_name}: ${(percentage as number).toFixed(1)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="workshop_count"
              nameKey="team_name"
            >
              {resolvedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={colors[index % colors.length]} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                `${value} workshops (${props.payload.percentage.toFixed(1)}%)`,
                'Workshop Count'
              ]}
              labelFormatter={(label) => `Team: ${label}`}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Workload Breakdown</h4>
          <div className="space-y-2">
            {[...resolvedData]
              .sort((a, b) => b.workshop_count - a.workshop_count)
              .map((item, index) => {
                const workload = getWorkloadLevel(item.workload_score);
                return (
                  <div key={`${item.team_name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: colors[index % colors.length] }}
                      ></div>
                      <span className="text-sm font-medium">{item.team_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.workshop_count}</span> workshops
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: workload.color + '20', 
                        color: workload.color 
                      }}>
                        {workload.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Workload Analysis</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Active</span>
                <span className="font-bold text-blue-600">
                  {resolvedData.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...resolvedData.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Balanced</span>
                <span className="font-bold text-green-600">
                  {resolvedData.reduce((best, item) => item.workload_score < best.workload_score ? item : best, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.min(...resolvedData.map(d => d.workload_score)).toFixed(1)}% workload
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Workshops</span>
                <span className="font-bold text-gray-900">{totalWorkshops}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across {data.length} teams
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamWorkloadDistributionChart;
